'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import {
  Users,
  Fuel,
  Settings2,
  Wifi,
  Wind,
  Music,
  Star,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

type Vehicle = {
  id: string;
  category: string;
  name: string;
  image: string;
  imageAlt: string;
  capacity: number;
  transmission: string;
  fuel: string;
  facilities: string[];
  priceFrom: string;
  priceLabel: string;
  available: boolean;
  badge?: string;
  description?: string;
  suitableFor?: string[];
};

const vehicles: Vehicle[] = [
  {
    id: 'veh-001',
    category: 'Premium',
    name: 'Toyota Alphard Gen 4',
    image: '/assets/images/ALPHARD HEV.jpeg',
    imageAlt: 'Toyota Alphard Gen 4 NAYVERA',
    capacity: 5,
    transmission: 'Matic',
    fuel: 'Bensin / Hybrid',
    facilities: ['AC Multi Zone', 'Ottoman Seat', 'Sunroof', 'Ambient Light', 'TV'],
    priceFrom: 'Rp 4.200.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'Eksklusif',
  },
  {
    id: 'veh-002',
    category: 'Premium',
    name: 'Toyota Alphard Facelift',
    image: '/assets/images/Alphard Facelift.jpeg',
    imageAlt: 'Toyota Alphard Facelift NAYVERA',
    capacity: 5,
    transmission: 'Matic',
    fuel: 'Bensin',
    facilities: ['AC Multi Zone', 'Ottoman Seat', 'Sunroof', 'Ambient Light', 'TV'],
    priceFrom: 'Rp 3.000.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'Premium',
  },
  {
    id: 'veh-003',
    category: 'Premium',
    name: 'Denza',
    image: '/assets/images/Denza.jpg',
    imageAlt: 'Denza NAYVERA',
    capacity: 5,
    transmission: 'Matic',
    fuel: 'Listrik / Hybrid',
    facilities: ['AC', 'Panoramic Roof', 'Leather Seat', 'Audio Premium', 'Wireless Charging'],
    priceFrom: 'Rp 4.000.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'EV',
  },
  {
    id: 'veh-004',
    category: 'Premium',
    name: 'Mercedes-Benz Sprinter',
    image: '/assets/images/Sprinter.jpg',
    imageAlt: 'Mercedes-Benz Sprinter NAYVERA',
    capacity: 9,
    transmission: 'Manual',
    fuel: 'Solar',
    facilities: ['AC', 'Reclining Seat', 'TV', 'WiFi', 'USB Charging', 'Curtain'],
    priceFrom: 'Hubungi Kami',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'VIP',
  },
  {
    id: 'veh-005',
    category: 'Premium',
    name: 'Toyota Fortuner',
    image: '/assets/images/Fortuner.jpeg',
    imageAlt: 'Toyota Fortuner NAYVERA',
    capacity: 6,
    transmission: 'Matic',
    fuel: 'Solar',
    facilities: ['AC', 'Leather Seat', 'Kamera 360°', '4WD'],
    priceFrom: 'Rp 1.700.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'Premium',
  },
  {
    id: 'veh-006',
    category: 'Premium',
    name: 'Mitsubishi Pajero Sport',
    image: '/assets/images/Pajero.jpg',
    imageAlt: 'Mitsubishi Pajero Sport NAYVERA',
    capacity: 6,
    transmission: 'Matic',
    fuel: 'Solar',
    facilities: ['AC Dual Zone', 'Kamera 360°', 'Apple CarPlay'],
    priceFrom: 'Rp 1.700.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
  {
    id: 'veh-007',
    category: 'Premium',
    name: 'Hyundai Ioniq 5',
    image: '/assets/images/Ioniq 5.jpg',
    imageAlt: 'Hyundai Ioniq 5 NAYVERA',
    capacity: 5,
    transmission: 'Matic',
    fuel: 'Listrik (EV)',
    facilities: ['AC', 'Panoramic Roof', 'V2L', 'BOSE Audio', 'Wireless Charging'],
    priceFrom: 'Rp 3.000.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'EV',
  },
  {
    id: 'veh-008',
    category: 'Premium',
    name: 'BMW 528i',
    image: '/assets/images/BMW 528i.jpg',
    imageAlt: 'BMW 528i NAYVERA',
    capacity: 4,
    transmission: 'Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Leather Seat', 'Harman Kardon', 'Apple CarPlay'],
    priceFrom: 'Rp 1.800.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'Mewah',
  },
  {
    id: 'veh-009',
    category: 'Executive',
    name: 'Toyota Innova Zenix',
    image: '/assets/images/ZENIX G.jpeg',
    imageAlt: 'Toyota Innova Zenix NAYVERA',
    capacity: 7,
    transmission: 'Matic',
    fuel: 'Bensin / Hybrid',
    facilities: ['AC Dual Zone', 'Kamera 360°', 'BSM'],
    priceFrom: 'Rp 850.000',
    priceLabel: '12 jam · Lepas kunci',
    available: true,
    badge: 'Baru',
  },
  {
    id: 'veh-010',
    category: 'Executive',
    name: 'Toyota Innova Reborn',
    image: '/assets/images/REBORN.jpeg',
    imageAlt: 'Toyota Innova Reborn NAYVERA',
    capacity: 7,
    transmission: 'Manual / Matic',
    fuel: 'Solar',
    facilities: ['AC', 'Audio', 'Power Window', 'Kamera Mundur'],
    priceFrom: 'Rp 750.000',
    priceLabel: '12 jam · Lepas kunci',
    available: true,
    badge: 'Populer',
  },
  {
    id: 'veh-011',
    category: 'Executive',
    name: 'Toyota Innova Venturer',
    image: '/assets/images/Venturer.jpeg',
    imageAlt: 'Toyota Innova Venturer NAYVERA',
    capacity: 6,
    transmission: 'Matic',
    fuel: 'Solar',
    facilities: ['AC', 'Leather Seat', 'Kamera Mundur'],
    priceFrom: 'Rp 950.000',
    priceLabel: '12 jam · Lepas kunci',
    available: true,
  },
  {
    id: 'veh-012',
    category: 'Executive',
    name: 'Neta V-II',
    image: '/assets/images/Neta V-II.jpg',
    imageAlt: 'Neta V-II NAYVERA',
    capacity: 5,
    transmission: 'Matic',
    fuel: 'Listrik (EV)',
    facilities: ['AC', 'Touchscreen', 'Kamera Mundur', 'Wireless Charging'],
    priceFrom: '850.000',
    priceLabel: '12 jam · lepas kunci',
    available: false,
    badge: 'EV',
  },
  {
    id: 'veh-013',
    category: 'Executive',
    name: 'Wuling AirEV',
    image: '/assets/images/Wuling AirEV.jpg',
    imageAlt: 'Wuling AirEV NAYVERA',
    capacity: 4,
    transmission: 'Matic',
    fuel: 'Listrik (EV)',
    facilities: ['AC', 'Touchscreen', 'Bluetooth', 'USB Charging'],
    priceFrom: 'Rp 498.000',
    priceLabel: '12 jam · lepas kunci',
    available: true,
    badge: 'EV',
  },
  {
    id: 'veh-014',
    category: 'Executive',
    name: 'Wuling Binguo EV',
    image: '/assets/images/Bingou EV.jpg',
    imageAlt: 'Wuling Binguo EV NAYVERA',
    capacity: 5,
    transmission: 'Matic',
    fuel: 'Listrik (EV)',
    facilities: ['AC', 'Touchscreen', 'Kamera Mundur', 'USB Charging'],
    priceFrom: '850.000',
    priceLabel: '12 jam · lepas kunci',
    available: false,
    badge: 'EV',
  },
  {
    id: 'veh-015',
    category: 'Reguler',
    name: 'Mitsubishi Xpander',
    image: '/assets/images/Xpander.jpg',
    imageAlt: 'Mitsubishi Xpander NAYVERA',
    capacity: 7,
    transmission: 'Manual / Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window', 'Kamera Mundur'],
    priceFrom: 'Rp 650.000',
    priceLabel: '12 jam · lepas kunci',
    available: true,
  },
  {
    id: 'veh-016',
    category: 'Reguler',
    name: 'Toyota Avanza',
    image: '/assets/images/Avanza.jpeg',
    imageAlt: 'Toyota Avanza NAYVERA',
    capacity: 7,
    transmission: 'Manual / Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window'],
    priceFrom: 'Rp 598.000',
    priceLabel: '12 jam · lepas kunci',
    available: true,
  },
  {
    id: 'veh-017',
    category: 'Reguler',
    name: 'Toyota Xenia',
    image: '/assets/images/xenia.jpg',
    imageAlt: 'Toyota Xenia NAYVERA',
    capacity: 7,
    transmission: 'Manual / Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window'],
    priceFrom: 'Rp 550.000',
    priceLabel: '12 jam · lepas kunci',
    available: true,
  },
  {
    id: 'veh-018',
    category: 'Reguler',
    name: 'Honda Jazz',
    image: '/assets/images/Honda Jazz.jpg',
    imageAlt: 'Honda Jazz NAYVERA',
    capacity: 5,
    transmission: 'Manual / Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window', 'Kamera Mundur'],
    priceFrom: 'Rp 550.000',
    priceLabel: '12 jam · lepas kunci',
    available: false,
  },
  {
    id: 'veh-019',
    category: 'Reguler',
    name: 'Honda Brio',
    image: '/assets/images/Brio.jpg',
    imageAlt: 'Honda Brio NAYVERA',
    capacity: 5,
    transmission: 'Manual / Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window'],
    priceFrom: 'Rp 498.000',
    priceLabel: '12 jam · lepas kunci',
    available: false,
  },
  {
    id: 'veh-020',
    category: 'Reguler',
    name: 'Toyota Calya / Daihatsu Sigra',
    image: '/assets/images/Sigra dan Calya.jpg',
    imageAlt: 'Toyota Calya Daihatsu Sigra NAYVERA',
    capacity: 7,
    transmission: 'Manual / Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window'],
    priceFrom: 'Rp 450.000',
    priceLabel: '12 jam · lepas kunci',
    available: true,
  },
  {
    id: 'veh-021',
    category: 'Reguler',
    name: 'Toyota Agya / Daihatsu Ayla',
    image: '/assets/images/AYLA DAN AGYA.jpg',
    imageAlt: 'Toyota Agya Daihatsu Ayla NAYVERA',
    capacity: 5,
    transmission: 'Manual / Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window'],
    priceFrom: 'Rp 298.000',
    priceLabel: '12 jam · lepas kunci',
    available: false,
  },
  {
    id: 'veh-022',
    category: 'Reguler',
    name: 'Suzuki Ertiga',
    image: '/assets/images/Ertiga.jpg',
    imageAlt: 'Suzuki Ertiga NAYVERA',
    capacity: 7,
    transmission: 'Manual / Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window', 'Kamera Mundur'],
    priceFrom: 'Rp. 598,000',
    priceLabel: '12 jam · lepas kunci',
    available: true,
  },
  {
    id: 'veh-023',
    category: 'Reguler',
    name: 'Toyota Raize',
    image: '/assets/images/Raize.jpg',
    imageAlt: 'Toyota Raize NAYVERA',
    capacity: 5,
    transmission: 'Matic',
    fuel: 'Bensin',
    facilities: ['AC', 'Audio', 'Power Window', 'Kamera Mundur'],
    priceFrom: 'Rp 498.000',
    priceLabel: '12 jam · lepas kunci',
    available: true,
  },
  {
    id: 'veh-024',
    category: 'Bus',
    name: 'Big Bus 50–59 Seat',
    image: '/assets/images/BIG_BUS.jpeg',
    imageAlt: 'Big Bus 50–59 Seat NAYVERA',
    capacity: 59,
    transmission: 'Manual',
    fuel: 'Solar',
    facilities: ['AC', 'Reclining Seat', 'TV', 'Audio', 'Mikrofon', 'Toilet', 'Bagasi Bawah'],
    priceFrom: 'Rp 3.500.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'Kapasitas Besar',
  },
  {
    id: 'veh-025',
    category: 'Bus',
    name: 'Medium Bus 31–35 Seat',
    image: '/assets/images/Medium Bus.jpeg',
    imageAlt: 'Medium Bus 31–35 Seat NAYVERA',
    capacity: 35,
    transmission: 'Manual',
    fuel: 'Solar',
    facilities: ['AC', 'Reclining Seat', 'Audio', 'Mikrofon', 'Bagasi Bawah'],
    priceFrom: 'Rp 2.500.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
  {
    id: 'veh-026',
    category: 'Bus',
    name: 'Elf Long 19 Seat',
    image: '/assets/images/ELF.jpeg',
    imageAlt: 'Elf Long 19 Seat NAYVERA',
    capacity: 19,
    transmission: 'Manual',
    fuel: 'Solar',
    facilities: ['AC', 'Audio', 'Karaoke', 'Power Window'],
    priceFrom: 'Rp 1.600.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
  {
    id: 'veh-027',
    category: 'Bus',
    name: 'Toyota Hiace Premio 14 Seat',
    image: '/assets/images/Hiace-Premio.jpeg',
    imageAlt: 'Toyota Hiace Premio 14 Seat NAYVERA',
    capacity: 14,
    transmission: 'Matic',
    fuel: 'Solar',
    facilities: ['AC Dual', 'Reclining Seat', 'Audio', 'Curtain', 'USB Charging'],
    priceFrom: 'Rp 1.850.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'Premium',
  },
  {
    id: 'veh-028',
    category: 'Bus',
    name: 'Toyota Hiace Commuter 15 Seat',
    image: '/assets/images/HIACE_COMMUTER.jpeg',
    imageAlt: 'Toyota Hiace Commuter 15 Seat NAYVERA',
    capacity: 15,
    transmission: 'Manual',
    fuel: 'Solar',
    facilities: ['AC', 'Audio', 'Power Window'],
    priceFrom: 'Rp 1.550.000',
    priceLabel: '12 jam · dengan driver',
    available: true,
    badge: 'Populer',
  },
  {
    id: 'veh-029',
    category: 'Commercial',
    name: 'Toyota Hilux DC',
    image: '/assets/images/Hilux DC.jpg',
    imageAlt: 'Toyota Hilux DC NAYVERA',
    capacity: 5,
    transmission: 'Manual / Matic',
    fuel: 'Solar',
    facilities: ['AC', 'Audio', 'Power Window', 'Bak Belakang'],
    priceFrom: 'Hubungi Kami',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
  {
    id: 'veh-030',
    category: 'Commercial',
    name: 'Mitsubishi Triton DC',
    image: '/assets/images/Triton DC.jpg',
    imageAlt: 'Mitsubishi Triton DC NAYVERA',
    capacity: 5,
    transmission: 'Manual',
    fuel: 'Solar',
    facilities: ['AC', 'Audio', 'Power Window', 'Bak Belakang'],
    priceFrom: 'Hubungi Kami',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
  {
    id: 'veh-031',
    category: 'Commercial',
    name: 'Double Engkel Box (CDD Box) 5.000 kg',
    image: '/assets/images/Double Engkel Box (CDD Box).jpg',
    imageAlt: 'Double Engkel Box CDD Box NAYVERA',
    capacity: 3,
    transmission: 'Manual',
    fuel: 'Solar',
    facilities: ['Box Tertutup', 'Kapasitas 5.000 kg'],
    priceFrom: 'Hubungi Kami',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
  {
    id: 'veh-032',
    category: 'Commercial',
    name: 'Engkel Box (CDE Box) 2.200 kg',
    image: '/assets/images/Engkel Box (CDE Box).jpg',
    imageAlt: 'Engkel Box CDE Box NAYVERA',
    capacity: 3,
    transmission: 'Manual',
    fuel: 'Solar',
    facilities: ['Box Tertutup', 'Kapasitas 2.200 kg'],
    priceFrom: 'Hubungi Kami',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
  {
    id: 'veh-033',
    category: 'Commercial',
    name: 'Box Kecil 1.000 kg',
    image: '/assets/images/Box Kecil.jpg',
    imageAlt: 'Box Kecil 1.000 kg NAYVERA',
    capacity: 3,
    transmission: 'Manual',
    fuel: 'Bensin / Solar',
    facilities: ['Box Tertutup', 'Kapasitas 1.000 kg'],
    priceFrom: 'Hubungi Kami',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
  {
    id: 'veh-034',
    category: 'Commercial',
    name: 'Blind Van 720 kg',
    image: '/assets/images/Blind Van.jpg',
    imageAlt: 'Blind Van 720 kg NAYVERA',
    capacity: 2,
    transmission: 'Manual',
    fuel: 'Bensin / Solar',
    facilities: ['Ruang Kargo Tertutup', 'Kapasitas 720 kg'],
    priceFrom: 'Hubungi Kami',
    priceLabel: '12 jam · dengan driver',
    available: true,
  },
];

const categories = [
  'Semua',
  'Premium',
  'Executive',
  'Reguler',
  'Bus',
  'Commercial',
];

const facilityIcons: Record<string, React.ReactNode> = {
  WiFi: <Wifi size={12} />,
  AC: <Wind size={12} />,
  Audio: <Music size={12} />,
};

export default function FleetCatalog() {
  const [activeCategory, setActiveCategory] = useState('Semua');

  const filtered =
    activeCategory === 'Semua'
      ? vehicles
      : vehicles.filter((v) => v.category === activeCategory);

  const getVehicleDescription = (vehicle: Vehicle) => {
    if (vehicle.description) return vehicle.description;

    const descriptions: Record<string, string> = {
      Premium: `Pilihan kendaraan premium untuk perjalanan yang mengutamakan kenyamanan, privasi, dan pengalaman berkendara yang lebih eksklusif. Cocok untuk kebutuhan bisnis, perjalanan VIP, keluarga, maupun agenda khusus.`,
      Executive: `Kendaraan yang nyaman dan praktis untuk perjalanan keluarga, bisnis, maupun kebutuhan mobilitas harian. Pilihan tepat untuk Anda yang menginginkan kenyamanan dengan fleksibilitas layanan.`,
      Reguler: `Pilihan kendaraan praktis untuk kebutuhan perjalanan sehari-hari, keluarga, bisnis, maupun perjalanan dalam dan luar kota dengan kenyamanan yang tetap terjaga.`,
      Bus: `Pilihan kendaraan untuk perjalanan bersama rombongan dengan kapasitas besar. Cocok untuk wisata, kegiatan perusahaan, sekolah, keluarga, acara, hingga perjalanan luar kota.`,
      Commercial: `Kendaraan untuk mendukung kebutuhan operasional, distribusi, logistik, maupun pengangkutan barang dengan pilihan kapasitas dan konfigurasi yang disesuaikan dengan kebutuhan.`,
    };

    return descriptions[vehicle.category] || `Kendaraan yang dapat disesuaikan untuk kebutuhan perjalanan dan mobilitas Anda bersama NAYVERA INDO TRANSPORT.`;
  };

  const getSuitableFor = (vehicle: Vehicle) => {
    if (vehicle.suitableFor?.length) return vehicle.suitableFor;

    const suitableByCategory: Record<string, string[]> = {
      Premium: ['Perjalanan VIP', 'Perjalanan bisnis', 'Keluarga', 'Antar-jemput', 'Acara khusus'],
      Executive: ['Perjalanan keluarga', 'Perjalanan bisnis', 'Wisata & liburan', 'Antar-jemput', 'Perjalanan dalam kota'],
      Reguler: ['Perjalanan keluarga', 'Kebutuhan harian', 'Perjalanan bisnis', 'Wisata & liburan', 'Perjalanan luar kota'],
      Bus: ['Perjalanan rombongan', 'Wisata & liburan', 'Kegiatan perusahaan', 'Sekolah & kampus', 'Acara keluarga', 'Perjalanan luar kota'],
      Commercial: ['Distribusi barang', 'Logistik', 'Operasional perusahaan', 'Pengangkutan barang', 'Kebutuhan proyek'],
    };

    return suitableByCategory[vehicle.category] || ['Kebutuhan perjalanan dan mobilitas'];
  };

  const getServiceLabel = (vehicle: Vehicle) => {
    const label = vehicle.priceLabel.toLowerCase();
    if (label.includes('lepas kunci')) return 'Lepas Kunci';
    if (label.includes('dengan driver')) return 'Dengan Driver';
    return vehicle.priceLabel.replace(/^12 jam\s*[·•-]?\s*/i, '').trim() || 'Hubungi NAYVERA';
  };

  const buildWaMessage = (vehicle: Vehicle) => {
    const facilities = vehicle.facilities.map((item) => `• ${item}`).join('\n');
    const suitableFor = getSuitableFor(vehicle).map((item) => `• ${item}`).join('\n');

    const msg = `Halo NAYVERA INDO TRANSPORT

Saya tertarik dengan kendaraan *${vehicle.name}* dan ingin mendapatkan informasi lebih lanjut mengenai ketersediaan unit serta layanan yang tersedia.

TENTANG KENDARAAN
${getVehicleDescription(vehicle)}

INFORMASI KENDARAAN
• Kendaraan: ${vehicle.name}
• Kategori: ${vehicle.category}
• Kapasitas: ${vehicle.capacity} penumpang
• Transmisi: ${vehicle.transmission}
• Bahan Bakar: ${vehicle.fuel}
• Layanan: ${getServiceLabel(vehicle)}

FASILITAS
${facilities}

COCOK UNTUK
${suitableFor}

INFORMASI PERJALANAN
• Tanggal penggunaan: Belum ditentukan
• Jam penjemputan: Belum ditentukan
• Tanggal selesai: Belum ditentukan
• Jam selesai: Belum ditentukan
• Lokasi penjemputan: Belum ditentukan
• Tujuan / Rute: Belum ditentukan
• Lokasi finish: Belum ditentukan
• Jumlah penumpang: Belum ditentukan

Mohon dibantu informasi mengenai ketersediaan unit dan detail layanan untuk kendaraan tersebut. Jika ada pilihan paket atau ketentuan penggunaan, mohon diinformasikan juga.

Terima kasih
NAYVERA INDO TRANSPORT`;

    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">

        <div className="flex gap-2 flex-wrap mb-10 p-1 bg-muted rounded-xl w-fit">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeCategory === cat
                  ? 'tab-active shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-white'
              }`}
            >
              {cat}

              {cat !== 'Semua' && (
                <span
                  className={`ml-2 text-xs rounded-full px-1.5 py-0.5 ${
                    activeCategory === cat
                      ? 'bg-primary/20 text-primary'
                      : 'bg-border text-muted-foreground'
                  }`}
                >
                  {vehicles.filter((v) => v.category === cat).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="mb-6 text-muted-foreground text-sm">
          Menampilkan{' '}
          <span className="font-bold text-foreground">
            {filtered.length}
          </span>{' '}
          kendaraan

          {activeCategory !== 'Semua' && (
            <span>
              {' '}
              dalam kategori{' '}
              <span className="text-accent font-semibold">
                {activeCategory}
              </span>
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((vehicle) => (
            <div
              key={vehicle.id}
              className="vehicle-card flex flex-col"
            >
              <div className="relative h-48 overflow-hidden">
                <AppImage
                  src={vehicle.image}
                  alt={vehicle.imageAlt}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-500"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent" />

                <div
                  className={`absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full ${
                    vehicle.available
                      ? 'bg-green-500 text-white'
                      : 'bg-red-500 text-white'
                  }`}
                >
                  {vehicle.available
                    ? 'Tersedia'
                    : 'Sedang Digunakan'}
                </div>

                {vehicle.badge && (
                  <div className="absolute top-3 right-3 bg-accent text-accent-foreground text-xs font-bold px-2.5 py-1 rounded-full">
                    {vehicle.badge}
                  </div>
                )}

                <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white text-xs font-semibold">
                  <Users size={13} />
                  {vehicle.capacity} Penumpang
                </div>
              </div>

              <div className="p-4 flex flex-col flex-1">

                <div className="mb-1">
                  <span className="text-xs font-semibold text-accent uppercase tracking-wider">
                    {vehicle.category}
                  </span>
                </div>

                <h3 className="font-bold text-foreground text-base mb-3 leading-snug">
                  {vehicle.name}
                </h3>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="flex items-center gap-1.5 bg-muted rounded-lg px-2.5 py-1.5">
                    <Settings2
                      size={12}
                      className="text-muted-foreground"
                    />
                    <span className="text-xs text-muted-foreground font-medium truncate">
                      {vehicle.transmission}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 bg-muted rounded-lg px-2.5 py-1.5">
                    <Fuel
                      size={12}
                      className="text-muted-foreground"
                    />
                    <span className="text-xs text-muted-foreground font-medium truncate">
                      {vehicle.fuel}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {vehicle.facilities
                    .slice(0, 4)
                    .map((fac) => (
                      <span
                        key={`fac-${vehicle.id}-${fac}`}
                        className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-md font-medium"
                      >
                        {facilityIcons[fac] || <Star size={10} />}
                        {fac}
                      </span>
                    ))}

                  {vehicle.facilities.length > 4 && (
                    <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-md font-medium">
                      +{vehicle.facilities.length - 4} lagi
                    </span>
                  )}
                </div>

                <div className="mt-auto pt-3 border-t border-border flex items-center justify-between gap-3">

                  <div>
                    <div className="text-xs text-muted-foreground mb-0.5">
                      Mulai dari
                    </div>

                    <div className="text-lg font-extrabold text-foreground tabular-nums">
                      {vehicle.priceFrom}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {vehicle.priceLabel}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Link
                      href={`/booking-page?vehicle=${encodeURIComponent(
                        vehicle.name
                      )}`}
                      className="btn-primary px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap"
                    >
                      Booking
                    </Link>

                    <a
                      href={buildWaMessage(vehicle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1 bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 px-4 py-2 rounded-lg text-xs font-bold transition-colors"
                    >
                      <span>WhatsApp</span>
                    </a>
                  </div>

                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center p-10 bg-primary rounded-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-accent blur-3xl" />
          </div>

          <div className="relative z-10">
            <h3 className="text-2xl font-extrabold text-white mb-3">
              Tidak menemukan kendaraan yang sesuai?
            </h3>

            <p className="text-white/65 mb-6">
              Hubungi kami langsung untuk kebutuhan khusus atau armada
              dengan spesifikasi tertentu.
            </p>

            <a
              href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
                'Halo NAYVERA INDO TRANSPORT, saya ingin menanyakan kendaraan dengan spesifikasi khusus.'
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 hover:shadow-lg"
            >
              Konsultasi via WhatsApp
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}